import advancedFormat from "dayjs/plugin/advancedFormat";
import { useMemo, useRef } from "react";
import dayjs from "dayjs";

import useQueriedRecords from "../../../api/api-hooks/records/useQueriedRecords";
import { Intervals } from "../../../api/api-hooks/records/useQueriedRecords";
import { timeFrameFormatting } from "../../../utils/DashboardUtils";
import { currencyToReadable } from "../../../utils/DashboardUtils";
import { GetGroupByFieldAndType } from "../../../utils/ChartUtils";
import useFieldMap from "../../../hooks/useFieldMap";
import useModule from "../../../hooks/useModule";

dayjs.extend(advancedFormat);
interface Props {
  moduleAPIName: string;
  query: any;
  topLevelFilters: any[] | undefined;
  viewFilters: any[] | undefined;
  validator: (query: any, fieldTypeMap: { [key: string]: string }) => boolean;
  orderBy: "ASC" | "DESC" | null;
  record: any;
  queryKeyID: string;
}

const useChartData = ({
  moduleAPIName,
  query,
  topLevelFilters = undefined,
  viewFilters,
  validator,
  orderBy = null,
  record,
  queryKeyID,
}: Props) => {
  const fieldTypeMap = useFieldMap(moduleAPIName, "FieldType");
  const fieldMap = useFieldMap(moduleAPIName);
  const fieldNameMap = useFieldMap(moduleAPIName, "Name");
  const { getModule } = useModule();

  const variableValues = useMemo(() => {
    let vals: Record<string, any> = {};
    if (record) {
      vals["record"] = record;
    }

    return vals;
  }, [record]);

  const { records, recordsLoading } = useQueriedRecords({
    variableValues: variableValues,
    moduleAPIName: moduleAPIName,
    query: query,
    topLevelFilters: topLevelFilters,
    viewFilters: viewFilters,
    queryValidator: (query) => validator(query, fieldTypeMap),
    useDefaultVariables: true,
    queryKeyID: queryKeyID,
  });

  const recordsRef = useRef<Record<string, any>[]>();
  recordsRef.current = records;

  // It's a related by field (a single reference field which conatins nested data in it.)
  const { groupByField, groupByFieldType, groupByFieldName } = useMemo(() => {
    return GetGroupByFieldAndType({
      groupByField: query?.GroupBy?.Field,
      fieldMap,
      getModule,
    });
  }, [query?.GroupBy?.Field, fieldMap, getModule]);

  const aggregationField = query?.Aggregations?.[0]?.Field;
  const aggregationMethod = query?.Aggregations?.[0]?.Method;
  const aggregationChartColor = query?.Aggregations?.[0]?.ChartColor;
  const aggregationTextColor = query?.Aggregations?.[0]?.TextColor;
  const benchmarkLines = query?.BenchmarkLines;
  const stringifiedRecords = JSON.stringify(records);
  const interval: Intervals = query?.GroupBy?.Interval;
  const isLocation = query?.GroupBy?.Field?.includes(".Address");
  const aggregationFieldName = fieldNameMap[aggregationField];

  const data = useMemo(() => {
    let records = recordsRef.current;

    if (!records || !records?.length) return [];

    const aggregationFieldSchema = fieldMap[aggregationField];
    const aggregationFieldType = aggregationFieldSchema
      ? aggregationFieldSchema.FieldType
      : "";
    const isDateTime =
      groupByFieldType === "Date" || groupByFieldType === "DateTime";

    if (isDateTime && orderBy && orderBy === "ASC") {
      records = records.sort((a, b) => {
        const groupA = a?.GroupBy?.[groupByField];
        const groupB = b?.GroupBy?.[groupByField];

        // @ts-ignore
        return new Date(groupA) - new Date(groupB);
      });
    }

    if (isDateTime && orderBy && orderBy === "DESC") {
      records = records.sort((a, b) => {
        let groupA = a?.GroupBy?.[groupByField];
        let groupB = b?.GroupBy?.[groupByField];

        if (interval === "hour" && !groupA.includes(":00")) {
          groupA += ":00";
          groupB += ":00";
        }

        // @ts-ignore
        return new Date(groupB) - new Date(groupA);
      });
    }

    records = records?.map((r) => {
      const aggregationValue = r?.[aggregationMethod]?.[aggregationField];

      const dataObject = {
        [groupByField]: r?.GroupBy?.[groupByField] ?? null,
        [aggregationMethod]: aggregationValue,
        ParentRecord: r?.ParentRecord,
      };

      if (
        aggregationFieldType === "CurrencyInterval" ||
        aggregationFieldType === "Currency" ||
        aggregationFieldSchema?.CalculatedFieldData?.Output === "currency"
      ) {
        const readable = currencyToReadable(
          r?.[aggregationMethod]?.[aggregationField]
        );
        const val = r?.[aggregationMethod]?.[aggregationField].Value;
        dataObject[aggregationMethod] = val;
        dataObject.readable = readable;
      }

      if (
        aggregationFieldType === "Percent" ||
        aggregationFieldSchema?.CalculatedFieldData?.Output === "percentage"
      ) {
        dataObject.readable =
          Math.round(Number(dataObject[aggregationMethod]) * 10) / 10 + "%";
        dataObject[aggregationMethod] = Number(dataObject[aggregationMethod]);
      }

      if (groupByFieldType === "Tags") {
        dataObject.Name = r?.GroupBy?.Name;
      }

      if (groupByFieldType === "SingleReference") {
        dataObject.OriginalValue = r?.GroupBy?.[groupByField];
        let value = r?.GroupBy?.[groupByField]?._RecordName;

        dataObject[groupByField] = value ?? null;
      }

      if (groupByFieldType === "MultiReference") {
        dataObject.OriginalValue = [r?.GroupBy?.[groupByField]];
        dataObject[groupByField] =
          r?.GroupBy?.[groupByField]?._RecordName ?? null;
      }

      if (groupByFieldType === "MultiSelect") {
        dataObject[groupByField] = Array.isArray(dataObject[groupByField])
          ? dataObject[groupByField].join(", ")
          : dataObject[groupByField];
      }

      if (isLocation) {
        const fieldName = groupByField.split(".")[0];
        const subFieldName = groupByField.split(".")[2];
        dataObject[groupByField] =
          r?.GroupBy?.[fieldName] ??
          `No ${subFieldName === "StateProvince" ? "State" : subFieldName}`;
      }

      if (isDateTime) {
        dataObject[groupByField] = dayjs(r?.GroupBy?.[groupByField]).format(
          // @ts-ignore
          timeFrameFormatting[interval]
        );
      }

      return dataObject;
    });

    if (!isDateTime && orderBy && orderBy === "ASC") {
      records = records.sort((a, b) => {
        const groupA = a?.GroupBy?.[groupByField];
        const groupB = b?.GroupBy?.[groupByField];

        return groupA > groupB ? 1 : -1;
      });
    }

    if (!isDateTime && orderBy && orderBy === "DESC") {
      records = records.sort((a, b) => {
        const groupA = a?.GroupBy?.[groupByField];
        const groupB = b?.GroupBy?.[groupByField];
        return groupA < groupB ? 1 : -1;
      });
    }
    return records;
  }, [
    stringifiedRecords,
    aggregationField,
    aggregationMethod,
    groupByField,
    fieldTypeMap,
    orderBy,
    interval,
    isLocation,
  ]);

  return {
    chartData: data,
    chartError:
      !validator(query, fieldTypeMap) ||
      (!recordsLoading && (!records || !records.length)),
    chartLoading: recordsLoading,
    groupByField,
    groupByFieldType: groupByFieldType,
    groupByFieldName,
    aggregationField,
    aggregationMethod,
    aggregationFieldName,
    aggregationChartColor,
    aggregationTextColor,
    benchmarkLines,
  };
};

export default useChartData;
